import { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Shirt, BarChart3, Package, Truck, PackageCheck, MapPin, Building2, ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

type LaundryTab = 'panel' | 'prendas' | 'recepcion' | 'ordenes' | 'despacho' | 'recorridos' | 'clientes' | 'incidentes' | 'reportes'

const TABS: { id: LaundryTab; label: string; path: string; icon: ReactNode }[] = [
  { id: 'panel', label: 'Panel', path: '/traceability/laundry', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'prendas', label: 'Prendas', path: '/traceability/laundry/prendas', icon: <Shirt className="h-4 w-4" /> },
  { id: 'recepcion', label: 'Recepción', path: '/traceability/laundry/recepcion', icon: <Package className="h-4 w-4" /> },
  { id: 'ordenes', label: 'Órdenes', path: '/traceability/laundry/ordenes', icon: <Truck className="h-4 w-4" /> },
  { id: 'despacho', label: 'Despacho', path: '/traceability/laundry/despacho', icon: <PackageCheck className="h-4 w-4" /> },
  { id: 'recorridos', label: 'Recorridos', path: '/traceability/laundry/recorridos', icon: <MapPin className="h-4 w-4" /> },
  { id: 'clientes', label: 'Clientes', path: '/traceability/laundry/clientes', icon: <Building2 className="h-4 w-4" /> },
  { id: 'incidentes', label: 'Incidentes', path: '/traceability/laundry/incidentes', icon: <ShieldAlert className="h-4 w-4" /> },
  { id: 'reportes', label: 'Reportes', path: '/traceability/laundry/reportes', icon: <BarChart3 className="h-4 w-4" /> },
]

function resolveTab(pathname: string): LaundryTab {
  if (pathname.startsWith('/traceability/laundry/coleccion')) return 'recorridos'
  if (pathname.startsWith('/traceability/laundry/items')) return 'prendas'
  const match = TABS.slice().reverse().find((tab) => pathname.startsWith(tab.path))
  return (match?.id ?? 'panel') as LaundryTab
}

interface LaundryModuleShellProps {
  children: ReactNode
}

export function LaundryModuleShell({ children }: LaundryModuleShellProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = resolveTab(location.pathname)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shirt className="h-7 w-7 text-emerald-600" />
        <div>
          <h1 className="text-xl font-bold">Lavandería Industrial</h1>
          <p className="text-sm text-muted-foreground">Trazabilidad RFID de prendas · Gestión integral de ciclo de vida</p>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap border-b pb-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm rounded-t transition-colors',
              activeTab === tab.id
                ? 'border-b-2 border-emerald-500 text-emerald-700 font-medium bg-emerald-50 dark:bg-emerald-950/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {children}
    </div>
  )
}
